import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Card, Col, Container, Row, Spinner } from "reactstrap";
import Select from "react-select";

import {
  getBoleteraCatalogo,
  postBoleteraCorte,
  postBoleteraVenta,
} from "../../../../helpers/backend_helper";
import { Alert, ConfirmAlertHelper } from "../../../../helpers/alerts/alertHelper";
import { ESTADOS } from "../../SalidasStatus/statusHelper";
import {
  MODO_DISTANCIA,
  lineasDelCarrito,
  money,
  nuevoUuid,
  totales,
} from "./boleteraHelper";
import ZonaGrid from "./ZonaGrid";
import Tarifas from "./Tarifas";
import Carrito from "./Carrito";
import Comprobante from "./Comprobante";
import CorteComprobante from "./CorteComprobante";
import "../../SalidasStatus/SalidasStatus.css";
import "../OperadorDashboard.css";
import "./Boletera.css";

/*
  Boletera del operador, abierta desde el detalle de su salida.

  El precio que se ve aquí es informativo: el servidor lo vuelve a calcular al cobrar.
  Cada carrito lleva un client_uuid que se reusa en los reintentos y solo se rota tras un
  cobro exitoso, para que un doble toque o una reconexión no cobren dos veces.
*/
const Boletera = () => {
  document.title = "Boletera | Sistema de Logística";

  const { id } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [cobrando, setCobrando] = useState(false);
  const [error, setError] = useState(null);
  const [catalogo, setCatalogo] = useState(null);
  const [zonaOrigenId, setZonaOrigenId] = useState(null);
  const [zonaDestino, setZonaDestino] = useState(null);
  const [qty, setQty] = useState({});
  const [uuid, setUuid] = useState(nuevoUuid);
  const [venta, setVenta] = useState(null);
  const [corteHecho, setCorteHecho] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getBoleteraCatalogo(id, { withToast: false });
      if (response?.status) {
        setCatalogo(response);
        setZonaOrigenId(response.zona_origen?.id ?? null);
        setError(null);
      } else {
        setError(response?.message || "No se pudo abrir la boletera.");
      }
    } catch (e) {
      console.error(e);
      setError("No se pudo abrir la boletera.");
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const limpiarCarrito = () => setQty({});

  // Cambiar de destino o de origen cambia los precios, así que el carrito deja de ser válido
  const pickDestino = (zona) => {
    setZonaDestino(zona);
    limpiarCarrito();
  };

  const cambiarOrigen = (opcion) => {
    setZonaOrigenId(opcion?.id ?? null);
    setZonaDestino(null);
    limpiarCarrito();
  };

  const agregar = (tipoId, delta) =>
    setQty((prev) => {
      const siguiente = Math.max(0, (prev[tipoId] ?? 0) + delta);
      return { ...prev, [tipoId]: siguiente };
    });

  const lineas = useMemo(
    () => lineasDelCarrito(catalogo, zonaDestino, qty, zonaOrigenId),
    [catalogo, zonaDestino, qty, zonaOrigenId]
  );
  const { boletos, total } = totales(lineas);

  const cobrar = async () => {
    setCobrando(true);
    try {
      const response = await postBoleteraVenta(id, {
        client_uuid: uuid,
        zone_destino_id: zonaDestino.id,
        zone_origen_id: catalogo.modo === MODO_DISTANCIA ? zonaOrigenId : null,
        lineas: lineas.map((l) => ({ fare_type_id: l.tipo.id, cantidad: l.cantidad })),
      });

      if (response?.status) {
        setVenta(response.venta ?? { folio: response.folio, boletos, total, lineas: [] });
        limpiarCarrito();
        setUuid(nuevoUuid());
        cargar();
      }
    } catch (e) {
      console.error(e);
      Alert("No se pudo registrar la venta.", false);
    }
    setCobrando(false);
  };

  const corte = (tipo) =>
    ConfirmAlertHelper(
      async (confirmed) => {
        if (!confirmed) return;
        const response = await postBoleteraCorte({ tipo });
        if (response?.status) {
          setCorteHecho(response.corte);
          cargar();
        }
      },
      `Corte ${tipo}`,
      tipo === "parcial"
        ? "Se resumirá lo vendido desde tu último corte parcial. ¿Continuar?"
        : "Se resumirá todo lo que has vendido en el día. ¿Continuar?",
      "Hacer corte"
    );

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  if (!catalogo) {
    return (
      <div className="page-content">
        <Container fluid>
          <Card className="salidas-status-card operador-card">
            <div style={{ padding: "32px 22px", textAlign: "center" }}>
              <div className="text-muted" style={{ fontSize: "13px" }}>{error}</div>
              <Button color="light" size="sm" className="mt-3" onClick={() => history.push("/dashboard")}>
                Volver a mis salidas
              </Button>
            </div>
          </Card>
        </Container>
      </div>
    );
  }

  const resumen = catalogo.resumen_dia ?? {};
  const puedeCobrar = !cobrando && lineas.length > 0 && !!zonaDestino;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="12">
              <Card className="salidas-status-card operador-card">
                <div className="salidas-status-header">
                  <Button
                    color="light"
                    size="sm"
                    onClick={() => history.push(`/dashboard-operador/salida/${id}`)}
                  >
                    <i className="mdi mdi-arrow-left me-1"></i> Salida
                  </Button>
                  <div>
                    <div style={{ fontSize: "17px", fontWeight: 700, lineHeight: 1.2 }}>Boletera</div>
                    <div style={{ fontSize: "11.5px", color: "#777a89", marginTop: 2 }}>
                      {catalogo.salida?.ramal_nombre} · Unidad {catalogo.salida?.unidad ?? "-"} ·
                      Salida {catalogo.salida?.hora}
                      {catalogo.tramo && (
                        <span className="badge bg-light text-dark ms-2">
                          Tramo: {catalogo.tramo === "regreso" ? "Regreso" : "Ida"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: "11.5px", color: "#777a89" }}>
                    Hoy: {resumen.boletos ?? 0} boleto(s) · {money(resumen.total ?? 0)}
                  </span>
                  <div className="salidas-status-segmented">
                    <button type="button" onClick={() => corte("parcial")}>Corte parcial</button>
                    <button type="button" onClick={() => corte("total")}>Corte total</button>
                  </div>
                </div>

                <div style={{ padding: "16px 22px 20px" }}>
                  {catalogo.modo === MODO_DISTANCIA && (
                    <div style={{ maxWidth: 360, marginBottom: 16 }}>
                      <label className="form-label" style={{ fontSize: "12px" }}>
                        Zona de origen
                        {catalogo.zona_origen?.geocerca && (
                          <span style={{ color: "#777a89" }}> · sugerida por {catalogo.zona_origen.geocerca}</span>
                        )}
                      </label>
                      <Select
                        options={catalogo.zonas}
                        value={catalogo.zonas.find((z) => z.id === zonaOrigenId) ?? null}
                        getOptionLabel={(o) => o.nombre}
                        getOptionValue={(o) => `${o.id}`}
                        onChange={cambiarOrigen}
                        placeholder="Elegir zona de origen..."
                      />
                    </div>
                  )}

                  <Row>
                    <Col lg="7">
                      <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: 10 }}>Zona destino</div>
                      <ZonaGrid
                        catalogo={catalogo}
                        zonaOrigenId={zonaOrigenId}
                        seleccion={zonaDestino}
                        onPick={pickDestino}
                      />
                    </Col>

                    <Col lg="5">
                      <div style={{ fontSize: "14px", fontWeight: 600, margin: "10px 0" }}>
                        Tarifas
                        {zonaDestino && (
                          <span style={{ fontWeight: 400, color: "#777a89", fontSize: "12px" }}>
                            {" "}· hacia {zonaDestino.nombre}
                          </span>
                        )}
                      </div>
                      <Tarifas
                        catalogo={catalogo}
                        zonaDestino={zonaDestino}
                        zonaOrigenId={zonaOrigenId}
                        qty={qty}
                        onAdd={agregar}
                        disabled={!zonaDestino}
                      />

                      <div style={{ fontSize: "14px", fontWeight: 600, margin: "18px 0 6px" }}>Carrito</div>
                      <Carrito lineas={lineas} onAdd={agregar} />

                      <div className="boletera-cobro">
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: "11.5px", color: "#777a89" }}>
                            {boletos} boleto(s)
                            {zonaDestino ? ` · ${zonaDestino.nombre}` : ""}
                          </div>
                          <div style={{ fontSize: "22px", fontWeight: 700 }}>{money(total)}</div>
                        </div>
                        <Button
                          color="primary"
                          disabled={!puedeCobrar}
                          onClick={cobrar}
                          style={{ background: puedeCobrar ? ESTADOS.tie.ink : undefined }}
                        >
                          {cobrando ? <Spinner size="sm" className="me-1" /> : <i className="fa fa-cash-register me-1"></i>}
                          Cobrar e imprimir
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>

              <Comprobante
                venta={venta}
                salida={catalogo.salida}
                operador={catalogo.operador?.nombre}
                boleto={catalogo.boleto}
                onClose={() => setVenta(null)}
              />

              <CorteComprobante
                corte={corteHecho}
                salida={catalogo.salida}
                operador={catalogo.operador?.nombre}
                onClose={() => setCorteHecho(null)}
              />
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Boletera;
